/**
 * TimelineRegistry - Type Resolution System for Timeline Serialization
 *
 * This registry provides a central location for registering and resolving
 * Timeline component types during deserialization. It enables the importState
 * system to reconstruct the correct class instances from JSON state.
 */

import {
  SerializableState,
  ImportFunction
} from './types/serializable';

type MigrationFunction = (state: SerializableState) => SerializableState;

function getSettingsRecord(state: SerializableState): Record<string, unknown> {
  const candidate = (state as { settings?: unknown }).settings;
  return candidate !== null && typeof candidate === 'object' && !Array.isArray(candidate)
    ? candidate as Record<string, unknown>
    : {};
}

/**
 * Central registry for Timeline component type resolution
 */
export class TimelineRegistry {
  /**
   * Map of type names to import functions
   */
  private static constructors = new Map<string, ImportFunction>();

  /**
   * Map of type names to version migration functions
   */
  private static migrations = new Map<string, Map<string, MigrationFunction>>();

  /**
   * Map of type names to latest version
   */
  private static latestVersions = new Map<string, string>();

  /**
   * Register a type constructor
   * @param type - The type identifier
   * @param importFn - Function to construct instance from state
   */
  static register(type: string, importFn: ImportFunction): void {
    if (this.constructors.has(type)) {
      console.warn(`[TimelineRegistry] Overwriting existing type: ${type}`);
    }
    this.constructors.set(type, importFn);
    console.log(`[TimelineRegistry] Registered type: ${type}`);
  }

  /**
   * Register a version migration
   * @param type - The type identifier
   * @param fromVersion - Source version
   * @param toVersion - Target version
   * @param migrationFn - Migration function
   */
  static registerMigration(
    type: string,
    fromVersion: string,
    toVersion: string,
    migrationFn: MigrationFunction
  ): void {
    if (!this.migrations.has(type)) {
      this.migrations.set(type, new Map());
    }
    const key = `${fromVersion}->${toVersion}`;
    this.migrations.get(type)!.set(key, migrationFn);
    console.log(`[TimelineRegistry] Registered migration: ${type} ${key}`);

    // Update latest version if toVersion is newer
    const currentLatest = this.latestVersions.get(type);
    if (!currentLatest || this.compareVersions(toVersion, currentLatest) > 0) {
      this.latestVersions.set(type, toVersion);
    }
  }

  /**
   * Compare two version strings
   * @param a - First version
   * @param b - Second version
   * @returns >0 if a>b, <0 if a<b, 0 if equal
   */
  private static compareVersions(a: string, b: string): number {
    const aParts = a.split('.').map(Number);
    const bParts = b.split('.').map(Number);

    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const aVal = aParts[i] || 0;
      const bVal = bParts[i] || 0;
      if (aVal !== bVal) {
        return aVal - bVal;
      }
    }
    return 0;
  }

  /**
   * Import a component from serialized state
   * @param state - The serialized state
   * @returns The reconstructed component instance
   */
  static import<T = unknown>(state: SerializableState): T {
    // Apply any necessary migrations first
    const migratedState = this.applyMigrations(state);

    // Get the import function for this type
    const importFn = this.constructors.get(migratedState.type);
    if (!importFn) {
      throw new Error(
        `[TimelineRegistry] Unknown type: ${migratedState.type}. ` +
        `Available types: ${Array.from(this.constructors.keys()).join(', ')}`
      );
    }

    try {
      return importFn(migratedState) as T;
    } catch (error) {
      throw new Error(
        `[TimelineRegistry] Failed to import ${migratedState.type}`,
        { cause: error }
      );
    }
  }

  /**
   * Import multiple components
   * @param states - Array of serialized states
   * @returns Array of reconstructed components
   */
  static importMany<T = unknown>(states: SerializableState[]): T[] {
    return states.map(state => this.import<T>(state));
  }

  /**
   * Apply version migrations to a state object
   * @param state - The state to migrate
   * @returns The migrated state
   */
  private static applyMigrations(state: SerializableState): SerializableState {
    const typeMigrations = this.migrations.get(state.type);
    if (!typeMigrations) {
      return state; // No migrations for this type
    }

    let currentState = state;
    let currentVersion = state.version;
    const targetVersion = this.getLatestVersion(state.type);

    while (currentVersion !== targetVersion) {
      const nextVersion = this.getNextVersion(state.type, currentVersion);
      if (!nextVersion) {
        break; // No more migrations
      }

      const migrationKey = `${currentVersion}->${nextVersion}`;
      const migrationFn = typeMigrations.get(migrationKey);

      if (migrationFn) {
        console.log(`[TimelineRegistry] Migrating ${state.type} ${migrationKey}`);
        const migrated = migrationFn(currentState);
        // Guarantee version is bumped even if migrationFn forgets
        currentState = { ...migrated, version: nextVersion } as SerializableState;
        currentVersion = nextVersion;
      } else {
        console.warn(
          `[TimelineRegistry] No migration found for ${state.type} ${migrationKey}`
        );
        break;
      }
    }

    return currentState;
  }

  /**
   * Get the latest version for a type
   * @param type - The type identifier
   * @returns The latest version string
   */
  private static getLatestVersion(type: string): string {
    return this.latestVersions.get(type) || '0.0.0';
  }

  /**
   * Get the next version in the migration chain
   * @param type - The type identifier
   * @param currentVersion - The current version
   * @returns The next version or null
   */
  private static getNextVersion(type: string, currentVersion: string): string | null {
    const typeMigrations = this.migrations.get(type);
    if (!typeMigrations) {
      return null;
    }

    // Iterate through migration keys to find the next version
    for (const key of Array.from(typeMigrations.keys())) {
      const [from, to] = key.split('->');
      if (from === currentVersion) {
        return to;
      }
    }

    return null;
  }

  /**
   * Check if a type is registered
   * @param type - The type identifier
   * @returns True if the type is registered
   */
  static hasType(type: string): boolean {
    return this.constructors.has(type);
  }

  /**
   * Get all registered types
   * @returns Array of registered type names
   */
  static getRegisteredTypes(): string[] {
    return Array.from(this.constructors.keys());
  }

  /**
   * Clear all registrations (useful for testing)
   */
  static clear(): void {
    this.constructors.clear();
    this.migrations.clear();
    this.latestVersions.clear();
    console.log('[TimelineRegistry] Cleared all registrations');
  }

  /**
   * Validate that all required types are registered
   * @throws Error if any required types are missing
   */
  static validateRegistrations(): void {
    const requiredTypes = [
      'Timeline',
      'Scene',
      'VideoMedia',
      'ImageMedia',
      'TextMedia',
      'GeneratedSlide'
      // Overlay, text overlay, caption, and branding states are imported
      // directly by TimelineImpl because those item classes do not expose
      // registry importState methods yet.
    ];

    const missing = requiredTypes.filter(type => !this.hasType(type));
    if (missing.length > 0) {
      throw new Error(
        `[TimelineRegistry] Missing required types: ${missing.join(', ')}`
      );
    }

    console.log('[TimelineRegistry] All required types registered ✓');
  }
}

/**
 * Auto-registration helper
 * Call this after all Timeline components are loaded
 */
export function initializeTimelineRegistry(): void {
  console.log('[TimelineRegistry] Initializing...');

  // These will be registered by each component's module
  // Example:
  // TimelineRegistry.register('Timeline', TimelineImpl.importState);
  // TimelineRegistry.register('Scene', SceneItemImpl.importState);
  // etc.

  // Register any global migrations
  TimelineRegistry.registerMigration(
    'Timeline',
    '0.9.0',
    '1.0.0',
    (state: SerializableState): SerializableState => {
      const settings = getSettingsRecord(state);
      if (
        !Number.isFinite(settings.width) ||
        !Number.isFinite(settings.height) ||
        !Number.isFinite(settings.durationInFrames)
      ) {
        throw new Error('[TimelineRegistry] Timeline 0.9.0 state is missing required settings fields');
      }

      const backgroundColor = typeof settings.backgroundColor === 'string' && settings.backgroundColor
        ? settings.backgroundColor
        : '#000000';

      const migratedState = {
        ...state,
        version: '1.0.0',
        settings: {
          ...settings,
          backgroundColor
        }
      };

      return migratedState;
    }
  );

  console.log('[TimelineRegistry] Initialization complete');
}

// Export singleton instance for convenience
export default TimelineRegistry;
